import type { Capa, Operacion } from '@laser/core';

/** Colores de la vista previa por operación (fijos: la hoja siempre se ve clara). */
export const COLOR_OPERACION: Record<Operacion, string> = {
  corte: '#e00000',
  marcado: '#0047ff',
  grabado: '#111111',
  ignorar: '#9aa3ad',
};

const NOMBRES: Record<string, string> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', blue: '#0000ff', lime: '#00ff00', green: '#008000',
  yellow: '#ffff00', cyan: '#00ffff', aqua: '#00ffff', magenta: '#ff00ff', fuchsia: '#ff00ff', gray: '#808080',
  grey: '#808080', silver: '#c0c0c0', maroon: '#800000', navy: '#000080', orange: '#ffa500', purple: '#800080',
};

/** Cualquier color de SVG → "#rrggbb" en minúsculas; null si no pinta (none/transparent/url). */
export function normalizarColor(c: string | null | undefined): string | null {
  if (c == null) return null;
  const v = c.trim().toLowerCase();
  if (!v || v === 'none' || v === 'transparent' || v.startsWith('url(')) return null;
  if (NOMBRES[v]) return NOMBRES[v];
  let m = /^#([0-9a-f]{3})$/.exec(v);
  if (m) return '#' + m[1].split('').map((x) => x + x).join('');
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/.exec(v);
  if (m) return '#' + m[1];
  m = /^rgba?\(\s*([\d.]+%?)\s*[, ]\s*([\d.]+%?)\s*[, ]\s*([\d.]+%?)/.exec(v);
  if (m) {
    const canal = (s: string) => Math.max(0, Math.min(255, Math.round(s.endsWith('%') ? parseFloat(s) * 2.55 : parseFloat(s))));
    return '#' + [m[1], m[2], m[3]].map((s) => canal(s).toString(16).padStart(2, '0')).join('');
  }
  return v.startsWith('#') ? v : null;
}

function propiedad(el: Element, nombre: 'stroke' | 'fill'): string | null {
  for (let e: Element | null = el; e && e.nodeName.toLowerCase() !== 'svg'; e = e.parentElement) {
    const estilo = e.getAttribute('style');
    if (estilo) {
      const m = new RegExp(`(?:^|;)\\s*${nombre}\\s*:\\s*([^;]+)`, 'i').exec(estilo);
      if (m) return m[1].trim();
    }
    const attr = e.getAttribute(nombre);
    if (attr != null) return attr;
  }
  // Valores por omisión de SVG: fill negro, sin trazo.
  return nombre === 'fill' ? '#000000' : null;
}

const FORMAS = 'path,rect,circle,ellipse,line,polyline,polygon';
const NS_SVG = 'http://www.w3.org/2000/svg';

export function sanitizarSvg(doc: Document) {
  doc.querySelectorAll('script,foreignObject,iframe,object,embed').forEach((n) => n.remove());
  doc.querySelectorAll('*').forEach((el) => {
    for (const a of Array.from(el.attributes)) {
      const n = a.name.toLowerCase();
      if (n.startsWith('on')) el.removeAttribute(a.name);
      if ((n === 'href' || n === 'xlink:href') && /^\s*javascript:/i.test(a.value)) el.removeAttribute(a.name);
    }
  });
}

/**
 * Aplica la escala del diseño (1 = 100 %): mete el contenido en un <g transform="scale(…)">
 * y estira el viewBox, para que la vista mida lo que de verdad se va a cortar.
 * Con escala 100 % no toca nada.
 */
export function escalarSvg(raiz: Element, escalaX = 1, escalaY = 1): void {
  const ex = Number.isFinite(escalaX) && escalaX > 0 ? escalaX : 1;
  const ey = Number.isFinite(escalaY) && escalaY > 0 ? escalaY : 1;
  if (ex === 1 && ey === 1) return;
  const doc = raiz.ownerDocument;
  if (!doc) return;
  const g = doc.createElementNS(NS_SVG, 'g');
  g.setAttribute('transform', `scale(${ex} ${ey})`);
  while (raiz.firstChild) g.appendChild(raiz.firstChild);
  raiz.appendChild(g);
  const vb = (raiz.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  if (vb.length === 4 && vb.every((n) => Number.isFinite(n))) {
    const f = (n: number) => Math.round(n * 1000) / 1000;
    raiz.setAttribute('viewBox', `${f(vb[0] * ex)} ${f(vb[1] * ey)} ${f(vb[2] * ex)} ${f(vb[3] * ey)}`);
  }
}

/** Estilo de una ruta según el tipo de su capa y la operación elegida. */
export function estiloCapa(tipo: Capa['tipo'], op: Operacion): string[] {
  const e = ['vector-effect:non-scaling-stroke'];
  if (op === 'ignorar') return [...e, 'fill:none', `stroke:${COLOR_OPERACION.ignorar}`, 'stroke-dasharray:4 3', 'stroke-width:1px'];
  if (tipo === 'relleno' && op === 'grabado') return [...e, `fill:${COLOR_OPERACION.grabado}`, 'fill-opacity:.85', 'stroke:none'];
  const ancho = op === 'corte' ? 1.4 : op === 'grabado' ? 1.8 : 1.1;
  return [...e, 'fill:none', `stroke:${COLOR_OPERACION[op]}`, `stroke-width:${ancho}px`];
}

export interface OpcionesRecolor {
  grabarRasters?: boolean;
  resaltarCapa?: string | null;
  /** Escala del diseño, 1 = 100 % (como en Bambu Studio). */
  escalaX?: number;
  escalaY?: number;
}

/**
 * Pinta cada capa del SVG normalizado según la operación elegida. El SVG de `convert`
 * envuelve cada capa en <g id="{capa.id}">; las rutas fuera de esos grupos (SVG de otra
 * fuente) se asignan por color + tipo. Devuelve el SVG serializado, sin scripts.
 */
export function recolorearSvg(svgTexto: string, capas: Capa[], operaciones: Record<string, Operacion>, op: OpcionesRecolor = {}): string {
  const doc = new DOMParser().parseFromString(svgTexto, 'image/svg+xml');
  const raiz = doc.documentElement;
  if (!raiz || raiz.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) throw new Error('SVG inválido');
  sanitizarSvg(doc);
  const opDe = (c: Capa): Operacion => operaciones[c.id] ?? c.operacionSugerida;
  const pintar = (el: Element, capa: Capa) => {
    const estilos = estiloCapa(capa.tipo, opDe(capa));
    if (op.resaltarCapa && op.resaltarCapa !== capa.id) estilos.push('opacity:.18');
    el.setAttribute('style', estilos.join(';'));
    el.removeAttribute('stroke'); el.removeAttribute('fill');
    el.setAttribute('data-capa', capa.id);
  };

  const hechos = new Set<Element>();
  for (const capa of capas) {
    const g = raiz.querySelector(`[id="${capa.id.replace(/["\\]/g, '\\$&')}"]`);
    if (!g) continue;
    const formas = g.matches(FORMAS) ? [g] : Array.from(g.querySelectorAll(FORMAS));
    for (const el of formas) { pintar(el, capa); hechos.add(el); }
  }

  // Respaldo por color para rutas que no quedaron dentro de un grupo de capa.
  const porClave = new Map<string, Capa>();
  for (const c of capas) porClave.set(`${c.tipo}:${normalizarColor(c.color) ?? c.color.toLowerCase()}`, c);
  raiz.querySelectorAll(FORMAS).forEach((el) => {
    if (hechos.has(el)) return;
    const trazo = normalizarColor(propiedad(el, 'stroke'));
    const relleno = normalizarColor(propiedad(el, 'fill'));
    const capa = (trazo && porClave.get(`trazo:${trazo}`)) || (relleno && porClave.get(`relleno:${relleno}`)) || null;
    if (capa) { pintar(el, capa); return; }
    // Sin capa conocida: trazos como corte, rellenos sin pintar.
    el.setAttribute('style', trazo ? estiloCapa('trazo', 'corte').join(';') : 'fill:none;stroke:none');
  });

  if (op.grabarRasters === false) raiz.querySelectorAll('image').forEach((im) => im.setAttribute('opacity', '0.2'));
  escalarSvg(raiz, op.escalaX ?? 1, op.escalaY ?? 1);
  raiz.removeAttribute('width');
  raiz.removeAttribute('height');
  raiz.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  return new XMLSerializer().serializeToString(raiz);
}
